import {
  fetchAndActivate,
  getRemoteConfig,
  getString,
  RemoteConfig,
} from "firebase/remote-config";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getFirebaseApp } from "@risalabs_frontend_org/auth-wrapper";
import { logError } from "../utils/customLogger";

/**
 * Shape of all Remote Config parameters used by the app.
 * Add new keys here and in {@link DEFAULT_CONFIG} to expose them automatically.
 */
export interface RemoteConfigValues {
  [key: string]: string | number | boolean;
  /** Percentage (0-100) of RPA trigger clicks that show the writeback validation modal. */
  notes_qc_version_1_rollout: number;
}

/** Fallback values used when Remote Config has not been fetched yet or the fetch fails. */
const DEFAULT_CONFIG: RemoteConfigValues = {
  notes_qc_version_1_rollout: 20,
};

const REMOTE_CONFIG_KEYS = Object.keys(DEFAULT_CONFIG) as Array<
  keyof RemoteConfigValues
>;

const PROD_REFRESH_INTERVAL = 600000;
const DEV_REFRESH_INTERVAL = 10000;

let remoteConfigInstance: RemoteConfig | null = null;

/**
 * Returns a singleton Firebase {@link RemoteConfig} instance.
 * Initialises settings and default config on first call.
 *
 * Fetch throttle: 10 min in production, 10 s in development.
 */
function getRemoteConfigInstance(): RemoteConfig {
  if (!remoteConfigInstance) {
    remoteConfigInstance = getRemoteConfig(getFirebaseApp());
    remoteConfigInstance.settings.minimumFetchIntervalMillis =
      process.env.REACT_APP_ENV === "production"
        ? PROD_REFRESH_INTERVAL
        : DEV_REFRESH_INTERVAL;
    remoteConfigInstance.defaultConfig = DEFAULT_CONFIG;
  }
  return remoteConfigInstance;
}

/** Shape of the value exposed by {@link RemoteConfigContext}. */
interface RemoteConfigContextType {
  /** Current config values; defaults until the first successful fetch. */
  config: RemoteConfigValues;
  /** `true` while the initial or manual fetch is in progress. */
  loading: boolean;
  /** The error from the most recent fetch, or `null` if it succeeded. */
  error: Error | null;
  /** Manually re-fetch and activate remote config values. */
  refetch: () => Promise<void>;
}

const RemoteConfigContext = createContext<RemoteConfigContextType | undefined>(
  undefined,
);

/**
 * Provides Firebase Remote Config values to the component tree.
 *
 * On mount, calls `fetchAndActivate` to pull the latest server-side config.
 * Values are read as strings and fall back to {@link DEFAULT_CONFIG} when a key
 * is missing or empty. The fetch is throttled by `minimumFetchIntervalMillis`
 * (10 min prod / 10 s dev) so rapid re-mounts won't trigger redundant network
 * requests.
 *
 * Place this provider near the root of the app (inside Redux `<Provider>`) so
 * every downstream component can consume config via {@link useRemoteConfig}.
 *
 * @example
 * ```tsx
 * // index.tsx
 * <Provider store={store}>
 *   <RemoteConfigProvider>
 *     <App />
 *   </RemoteConfigProvider>
 * </Provider>
 * ```
 */
export const RemoteConfigProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [config, setConfig] = useState<RemoteConfigValues>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isMountedRef = useRef(true);

  const readAllValues = useCallback((rc: RemoteConfig): RemoteConfigValues => {
    const values = {} as RemoteConfigValues;
    for (const key of REMOTE_CONFIG_KEYS) {
      values[key] = getString(rc, key as string) || DEFAULT_CONFIG[key];
    }
    return values;
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rc = getRemoteConfigInstance();
      await fetchAndActivate(rc);
      if (isMountedRef.current) {
        setConfig(readAllValues(rc));
      }
    } catch (err) {
      logError(err as Error, "RemoteConfigProvider - fetchAndActivate");
      if (isMountedRef.current) {
        setError(err as Error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [readAllValues]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchConfig();
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchConfig]);

  const value = useMemo(
    () => ({ config, loading, error, refetch: fetchConfig }),
    [config, loading, error, fetchConfig],
  );

  return (
    <RemoteConfigContext.Provider value={value}>
      {children}
    </RemoteConfigContext.Provider>
  );
};

/**
 * Consumes the nearest {@link RemoteConfigProvider} and returns the current
 * remote config values, loading state, any fetch error, and a `refetch` handle.
 *
 * @throws If called outside of a `RemoteConfigProvider`.
 *
 * @example
 * ```tsx
 * const { config, loading } = useRemoteConfig();
 * if (!loading) {
 *   const shouldValidate = Math.random() * 100 < config.notes_qc_version_1_rollout;
 * }
 * ```
 */
export function useRemoteConfig(): RemoteConfigContextType {
  const context = useContext(RemoteConfigContext);
  if (!context) {
    throw new Error(
      "useRemoteConfig must be used within a RemoteConfigProvider",
    );
  }
  return context;
}
